import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { TaskGroupDialog } from 'src/app/modals/task-group-dialog/task-group-dialog.component';
import { TaskGroupAssignmentDialog } from 'src/app/modals/task-group-assignment-dialog/task-group-assignment-dialog.component';
import { ITaskSiteDialogData, TaskSiteDialogComponent } from 'src/app/modals/task-site-dialog/task-site-dialog.component';
import { TaskGroup } from 'src/app/models/taskGroup.model';
import { AuthService } from 'src/app/services';
import { TaskGroupService } from 'src/app/services/taskGroup.service';
import { TaskOrgLevelService } from 'src/app/services/task-org-level.service';
import { CommonService } from 'src/app/services/common.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { ToastService } from 'src/app/services/toast.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { segsRouteConfig } from 'src/app/shared/constants/segsRouteConfig.constants';
import { DeleteDialog, IDeleteDialogData } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { TranslateService } from '@ngx-translate/core';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';
import { TaskGroupDetailsPopupComponent } from 'src/app/common-components/task-group-details-popup/task-group-details-popup.component';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { ChildColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';

@Component({
  standalone: false,
    selector: 'app-task-list',
    templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './../../shared/grid/core-grid/core-grid.component.scss',
        './task-list.component.scss',
    ],
})
export class TaskListComponent extends CoreGridComponent<TaskGroup> implements OnInit, AfterViewInit {
    @ViewChild(FlyoutContainerComponent) override flyout!: FlyoutContainerComponent;

    constructor(
        protected taskGroupService: TaskGroupService,
        toastService: ToastService,
        commonService: CommonService,
        dialog: MatDialog,
        authService: AuthService,
        private organizationLevelService: OrganizationLevelService,
        private taskOrgLevelService: TaskOrgLevelService,
        gridViewService: GridViewService,
        private translateService: TranslateService,
        gridSettingsService: GridSettingsService,
        protected injector: Injector,
        @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData,
    ) {
        super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
        this.gridService = taskGroupService;
    }

    ngOnInit(): void {
        const searchDialogRef = this.injector.get<MatDialogRef<TaskListComponent> | null>(MatDialogRef, null);
        if (searchDialogRef) {
            this.searchDialogRef = searchDialogRef;
        }
        super.ngOnInit();
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.loadHeaderData();
    }

    protected get objectId(): string {
        return RolePermissionObjects.TaskGroup;
    }

    // Add To My Site and Remove write GLOBAL_TaskGroup_OrgLevel, and PermissionCheckInterceptor
    // authorizes each independently against that object (not GLOBAL_TaskGroup), so gate them on
    // the join entity's own Create/Delete flag rather than the parent grid's canCreate/canDelete.
    protected get canAddToMySite(): boolean {
        return this.permissionService.hasPermissionFlag(RolePermissionObjects.TaskGroupOrgLevel, gridToPermissionFlags[GridPermissions.Create]);
    }

    protected get canRemoveOrgLevel(): boolean {
        return this.permissionService.hasPermissionFlag(RolePermissionObjects.TaskGroupOrgLevel, gridToPermissionFlags[GridPermissions.Delete]);
    }

    protected override get isGridViewEnabled(): boolean { return true; }

    override get hasOrgFilter(): boolean {
        return true;
    }

    protected override getDefaultOrderByColumn(): string {
        return nameOf<TaskGroup>(x => x.name);
    }

    override async getColumnDetailsAsync(): Promise<{ [property: string]: ColumnDetails }> {
        const details = await super.getColumnDetailsAsync();
        
        // Ensure Tasks column (custom, not in backend config) is always visible
        if (!details['taskCount']) {
            details['taskCount'] = {
                displayName: 'Tasks',
                type: 'number',
                isNullable: true,
                isFilterable: false,
                isSortable: false,
                isDefault: true,
                nestedPath: undefined,
                dataType: 0,
                fieldType: 0,
                objectId: '',
                fieldName: '',
                fieldMetadata: undefined,
                isVisibleOnGrid: true,
                isVisibleOnDialog: true,
                isRequired: false,
                tooltip: undefined,
                placeholder: undefined,
                defaultValue: undefined,
                unit: undefined,
                isExportable: false,
                exportProperty: undefined,
                exportItemProperty: undefined,
            } as ColumnDetails;
        } else {
            details['taskCount'].isVisibleOnGrid = true;
            details['taskCount'].isDefault = true;
        }
        
        return details;
    }

    override initializeProperties(): void {
        this.addEntityTitle = 'Task Groups';
        this.searchPlaceholder = 'Search Task Groups';
        this.additionalActions = [
            {
                name: 'Assign',
                icon: 'assignment',
                action: (row: TaskGroup) => this.openTaskGroupAssignDialog(row.id),
            },
        ];
    }

    override initializeColumns(): void {
        this.columnConfigs = [
            {
                name: nameOf<TaskGroup>(x => x.name),
                displayName: 'Task Group Name',
                cssClass: () => 'left-aligned cursor-pointer',
                headerCssClass: () => 'left-aligned',
                onClick: (row: TaskGroup) => this.showTaskGroupDetails(row),
            },
            {
                name: 'taskCount',
                displayName: 'Tasks',
                isDisplayNameConfigurable: false,
                includeInGridSettings: false,
                isSortable: false,
                cellTemplate: (row: TaskGroup) => {
                    const taskCount = row.aptTasks?.length ?? 0;
                    return taskCount === 0 ? this.translateService.instant('No tasks') : `${taskCount}`;
                },
            },
            {
                name: nameOf<TaskGroup>(x => x.organizationLevelId),
                displayName: 'Visibility',
                isDisplayNameConfigurable: false,
                includeInGridSettings: false,
                isSortable: false,
                cellTemplate: (row: TaskGroup) => row.organizationLevelName ?? '-',
            },
            {
                name: nameOf<TaskGroup>(x => x.addedToMySite),
                displayName: 'Site Membership',
                isDisplayNameConfigurable: false,
                includeInGridSettings: false,
                isSortable: false,
                cellTemplate: () => '',
                children: (row: TaskGroup) => {
                    if (!row?.addedToMySite) {
                        if (!this.canAddToMySite) {
                            return [];
                        }
                        return [{
                            onClick: (r: TaskGroup) => this.openTaskSiteDialog('', r),
                            cssClass: () => 'task-btn main-btn',
                            cellTemplate: () => this.translateService.instant('Add To My Site'),
                        }];
                    }
                    // The association is hierarchy-wide (AddedToMySiteGridService) but Remove deletes
                    // at exactly the selected org level. When it's held at a descendant only, Remove
                    // would silently no-op, so route to the Task Group's details view instead.
                    if (!row.addedAtSelectedOrgLevel) {
                        return [{
                            onClick: (r: TaskGroup) => this.showTaskGroupDetails(r),
                            cssClass: () => 'main-btn task-added-btn task-added-btn-linked',
                            cellTemplate: () => this.translateService.instant('Added'),
                        }];
                    }

                    const children: ChildColumnConfig<TaskGroup>[] = [
                        {
                            cssClass: () => 'main-btn task-added-btn',
                            cellTemplate: () => this.translateService.instant('Added'),
                            readonly: () => true,
                        },
                    ];
                    if (this.canRemoveOrgLevel) {
                        children.push({
                            onClick: (r: TaskGroup) => this.openRemoveFromSiteDialog(r.id),
                            cssClass: () => 'task-btn task-remove-btn main-btn ml-2',
                            cellTemplate: () => this.translateService.instant('Remove'),
                        });
                    }
                    return children;
                },
            },
            { name: 'action', displayName: 'Actions' },
        ];
    }

    override objectFilterMap(propertyName: string): OrganizationLevelService | undefined {
        if (propertyName === nameOf<TaskGroup>(x => x.organizationLevelId)) {
            return this.organizationLevelService;
        }
        return undefined;
    }

    override initializeTabs() {
        return segsRouteConfig;
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override openEntityDialog(taskGroupId?: string): void {
        const model: IFormDialogData = { id: taskGroupId };
        this.openDialog(TaskGroupDialog, model);
    }

    override openEntityDeleteDialog(entity: TaskGroup): void {
        this.openDeleteDialog(DeleteDialog, entity, this.taskGroupService, 'Task Group');
    }

    showTaskGroupDetails(row: TaskGroup): void {
        this.openFlyoutInternal(TaskGroupDetailsPopupComponent, row, '600px', true);
    }

    openTaskSiteDialog(id: string, taskGroup: TaskGroup): void {
        const data: ITaskSiteDialogData = {
            id,
            taskGroupId: taskGroup.id,
            organizationLevelId: this.selectedOrg?.id,
        };
        this.openDialog(TaskSiteDialogComponent, data);
    }

    openRemoveFromSiteDialog(id: string): void {
        if (!this.selectedOrg?.id) return;
        const data: IDeleteDialogData = {
            id,
            formDeletes: true,
            customDelete: () => this.taskOrgLevelService.deleteById(id, this.selectedOrg.id),
            title: this.translateService.instant('Task Group from Site'),
            service: this.taskOrgLevelService,
        };
        const dialogRef = this.dialog.open(DeleteDialog, { data, width: '440px', disableClose: true });
        this.handleDialogClose(dialogRef);
    }

    openTaskGroupAssignDialog(taskGroupId: string): void {
        const model: IFormDialogData = { id: taskGroupId };
        this.openDialog(TaskGroupAssignmentDialog, model);
    }
}
